import React from 'react';

import { MAX_SKILL_PROMPT_LENGTH } from '../services/landingSkillRouting';
import type { ModelId } from '../services/modelCatalog';
import { isIOSApp } from '../utils/platformDetection';

import { ChatBoxInput } from './ChatBox';
import { FeatureSections } from './FeatureSections';
import { HumanAiSection } from './HumanAiSection';
import {
  landingChoiceAllowsMedia,
  landingPlaceholder,
  LandingSkillChoice,
} from './landingChoices';
import { LandingModeTabs } from './LandingModeTabs';
import { LandingChatsList, LandingLoopsList } from './LandingRecentLists';
import { LandingQuickNav } from './LandingQuickNav';
import { LandingSkillSelector } from './LandingSkillSelector';
import { MoatSections } from './MoatSections';
import { Navbar } from './Navbar';
import { PersonaSection } from './PersonaSection';
import { PlatformProof } from './PlatformProof';
import { ProcessSection } from './ProcessSection';
import { ProjectList, ProjectPreviewModeContext } from './ProjectList';
import { TrustSections } from './TrustSections';

/* The marketing sections below the fold are static: no props, no state, no
   effects. LandingPage re-renders on every keystroke in the chat input, so
   without this the full ~1,500 lines of section JSX were re-rendered per
   character typed. Memoising the block collapses that to nothing. */
const LandingSections: React.FC = React.memo(() => (
  <>
    <PlatformProof />
    <PersonaSection />
    <FeatureSections />
    <ProcessSection />
    <HumanAiSection />
    <MoatSections />
    <TrustSections />
  </>
));

LandingSections.displayName = 'LandingSections';

interface LandingPageLayoutProps {
  onModelChange?: (model: string) => void;
  modelValue?: ModelId;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendClick: (text: string, mediaUrls?: string[]) => Promise<boolean | void>;
  isSubmitting: boolean;
  submitError: string | null;
  isAuthenticated: boolean;
  authIsLoading: boolean;
  onConnectGitHub: () => void;
  selectedSkill: LandingSkillChoice;
  onSkillChange: (choice: LandingSkillChoice) => void;
  loopControls?: React.ReactNode;
}

/* No success banner: a successful send navigates straight to the planning page,
   so the only feedback worth rendering here is a failure. */
const Feedback: React.FC<{ error: string | null }> = ({ error }) => (
  <>
    {error && <div className="feedback error" role="alert">{error}</div>}
  </>
);

const LandingActions: React.FC<{
  authIsLoading: boolean;
  onConnectGitHub: () => void;
}> = ({ authIsLoading, onConnectGitHub }) => (
  <div className="landing-actions" style={authIsLoading ? { visibility: 'hidden' } : undefined}>
    <button type="button" className="landing-github-connect" onClick={onConnectGitHub} style={{ display: 'none' }}>
      <i className="fa-brands fa-github" />
      <span>Connect existing GitHub project</span>
    </button>
  </div>
);

const LandingCard: React.FC<{
  onModelChange?: (model: string) => void;
  modelValue?: ModelId;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendClick: (text: string, mediaUrls?: string[]) => Promise<boolean | void>;
  isSubmitting: boolean;
  submitError: string | null;
  onConnectGitHub: () => void;
  isAuthenticated: boolean;
  authIsLoading: boolean;
  selectedSkill: LandingSkillChoice;
  onSkillChange: (choice: LandingSkillChoice) => void;
  hideActions?: boolean;
  loopControls?: React.ReactNode;
}> = ({
  inputValue, onInputChange, onSendClick, isSubmitting, submitError,
  onConnectGitHub, isAuthenticated, authIsLoading, selectedSkill, onSkillChange,
  hideActions, loopControls, onModelChange, modelValue,
}) => {
  const showActions = !hideActions && (isAuthenticated || authIsLoading);

  return (
    <div className={`landing-card${showActions ? '' : ' landing-card--no-actions'}`}>
      <LandingModeTabs value={selectedSkill} onChange={onSkillChange} />
      <ChatBoxInput
        key={selectedSkill.kind}
        value={inputValue}
        onChange={onInputChange}
        onSend={onSendClick}
        isLoading={isSubmitting}
        projectId={null}
        isAnyAgentRunning={false}
        hasTodoTasks={false}
        isByokPlan={false}
        userMessageCount={0}
        allowMedia={landingChoiceAllowsMedia(selectedSkill)}
        maxInputLength={selectedSkill.kind === 'skill' ? MAX_SKILL_PROMPT_LENGTH : undefined}
        placeholder={landingPlaceholder(selectedSkill)}
        toolbarSlot={selectedSkill.kind === 'loop' ? loopControls : undefined}
        compactToolbar={selectedSkill.kind === 'loop'}
        onModelChange={onModelChange}
        modelValue={modelValue}
      />
      {showActions && <LandingActions authIsLoading={authIsLoading} onConnectGitHub={onConnectGitHub} />}
      <Feedback error={submitError} />
    </div>
  );
};


/* App mode's phone-shaped project previews are a phone layout; desktop keeps its cards. */
const PHONE_QUERY = '(max-width: 600px)';

const useIsPhone = (): boolean => {
  const [isPhone, setIsPhone] = React.useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.(PHONE_QUERY).matches),
  );

  React.useEffect(() => {
    const query = window.matchMedia?.(PHONE_QUERY);

    if (!query) return undefined;
    const update = () => setIsPhone(query.matches);

    update();
    query.addEventListener('change', update);

    return () => query.removeEventListener('change', update);
  }, []);

  return isPhone;
};

const LandingMain: React.FC<{
  onModelChange?: (model: string) => void;
  modelValue?: ModelId;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendClick: (text: string, mediaUrls?: string[]) => Promise<boolean | void>;
  isSubmitting: boolean;
  submitError: string | null;
  isAuthenticated: boolean;
  authIsLoading: boolean;
  onConnectGitHub: () => void;
  selectedSkill: LandingSkillChoice;
  onSkillChange: (choice: LandingSkillChoice) => void;
  loopControls?: React.ReactNode;
}> = (props) => {
  const iosApp = isIOSApp();
  const isPhone = useIsPhone();
  const heroPrefix = iosApp
    ? <>Plan with <span className="hero__highlight">AI Agents</span> to build</>
    : undefined;

  return (
    <main className="landing-main">
      <div id="home" className="landing-above-fold">
        <div className="landing-inner">
          <LandingSkillSelector prefix={heroPrefix} />
          <LandingCard
            inputValue={props.inputValue}
            onInputChange={props.onInputChange}
            onSendClick={props.onSendClick}
            isSubmitting={props.isSubmitting}
            submitError={props.submitError}
            onConnectGitHub={props.onConnectGitHub}
            isAuthenticated={props.isAuthenticated}
            authIsLoading={props.authIsLoading}
            selectedSkill={props.selectedSkill}
            onSkillChange={props.onSkillChange}
            hideActions={iosApp}
            loopControls={props.loopControls}
            onModelChange={props.onModelChange}
            modelValue={props.modelValue}
          />
        </div>
        <div id="projects">
          {/* Signed-in Loop and Chat modes list the user's own loops and chats; everyone else sees projects. */}
          {props.isAuthenticated && props.selectedSkill.kind === 'loop' && <LandingLoopsList />}
          {props.isAuthenticated && props.selectedSkill.kind === 'chat' && <LandingChatsList />}
          {!(props.isAuthenticated && (props.selectedSkill.kind === 'loop' || props.selectedSkill.kind === 'chat')) && (
            <ProjectPreviewModeContext.Provider value={isPhone && props.selectedSkill.id === 'app-builder' ? 'phone' : 'default'}>
              <ProjectList isAuthenticated={props.isAuthenticated} authIsLoading={props.authIsLoading} />
            </ProjectPreviewModeContext.Provider>
          )}
        </div>
      </div>
      {!iosApp && <LandingSections />}
    </main>
  );
};

export const LandingPageLayout: React.FC<LandingPageLayoutProps> = (props) => (
  <div className="landing-page">
    <Navbar authIsLoading={props.authIsLoading} />
    {!isIOSApp() && <LandingQuickNav isAuthenticated={props.isAuthenticated} />}
    <LandingMain
      inputValue={props.inputValue}
      onInputChange={props.onInputChange}
      onSendClick={props.onSendClick}
      isSubmitting={props.isSubmitting}
      submitError={props.submitError}
      isAuthenticated={props.isAuthenticated}
      authIsLoading={props.authIsLoading}
      onConnectGitHub={props.onConnectGitHub}
      selectedSkill={props.selectedSkill}
      onSkillChange={props.onSkillChange}
      loopControls={props.loopControls}
      onModelChange={props.onModelChange}
      modelValue={props.modelValue}
    />
  </div>
);
